import React, { useEffect, useState } from 'react';
import { TextField, useMediaQuery, useTheme } from '@mui/material';
import { useLoginContext } from '../../../../../contexts/LoginContext';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AdditionalEmailAddressesField: React.FC = () => {
  const { user, setUser, selectedProfileIndex } = useLoginContext();
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState('');

  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up('md'));
  const primaryEmail = user?.emailAddress;
  const value = React.useMemo(() => {
    return selectedProfileIndex !== undefined &&
      user.extProfiles[selectedProfileIndex]
      ? user.extProfiles[selectedProfileIndex].additionalEmailAddresses || []
      : [];
  }, [selectedProfileIndex, user.extProfiles]);

  useEffect(() => {
    const nonEmptyEmails = value.filter((email) => email.trim() !== '');
    let errorMsg = '';
    let hasError = false;

    if (primaryEmail && nonEmptyEmails.includes(primaryEmail)) {
      errorMsg =
        'Primary email address cannot be included in additional emails.';
      hasError = true;
    } else if (nonEmptyEmails.length > 0) {
      const isValidEmail = nonEmptyEmails.every((email) =>
        emailRegex.test(email)
      );
      const uniqueEmails = new Set(
        nonEmptyEmails.map((email) => email.toLowerCase())
      );
      const hasDuplicates = uniqueEmails.size !== nonEmptyEmails.length;
      if (!isValidEmail) {
        errorMsg = 'Please enter valid email addresses, separated by commas.';
        hasError = true;
      } else if (hasDuplicates) {
        errorMsg = 'Duplicate email addresses are not allowed.';
        hasError = true;
      }
    }
    if (value.length === 0) {
      hasError = false;
    }
    setError(hasError);
    setHelperText(
      hasError
        ? errorMsg
        : '(Not Required) Additional email addresses to receive notifications, separated by commas.'
    );
    // Do NOT call onChange here
  }, [value, primaryEmail]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emails = e.target.value.split(',').map((email) => email.trim());
    if (selectedProfileIndex !== undefined && user.extProfiles) {
      // Avoid creating a new object reference for selectedProfile, which can cause re-render loops
      const updatedProfiles = [...user.extProfiles];
      updatedProfiles[selectedProfileIndex] = {
        ...updatedProfiles[selectedProfileIndex],
        additionalEmailAddresses: emails,
      };
      setUser({ ...user, extProfiles: updatedProfiles });
    }
  };

  return (
    <TextField
      size="small"
      variant="outlined"
      fullWidth
      label="Additional Email Addresses"
      helperText={isMobile ? '' : helperText}
      value={value.join(',')}
      name="additionalEmailAddresses"
      onChange={handleChange}
      error={error}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
        },
        height: '100%',
      }}
    />
  );
};

export default AdditionalEmailAddressesField;
